"use client";

import { useEffect, useState } from "react";
import { useInView } from "@/hooks/useInView";
import { landingStats } from "./homepage-data";
import { LANDING_TOOL_COUNT } from "./LandingToolsGrid";

function parseStatValue(raw: string) {
  const match = raw.match(/^(\d+(?:\.\d+)?)(.*)/);
  if (!match) return { num: 0, decimals: 0, suffix: raw };
  const num = parseFloat(match[1]);
  const decimals = match[1].includes(".") ? match[1].split(".")[1].length : 0;
  return { num, decimals, suffix: match[2] };
}

function useCounter(target: number, isActive: boolean, duration = 1800) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isActive) return;
    let startTime: number | null = null;
    function step(ts: number) {
      startTime ??= ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - (1 - progress) ** 3;
      setCount(target * eased);
      if (progress < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }, [isActive, target, duration]);
  return count;
}

function AnimatedStat({ stat, isActive, delay }: { stat: (typeof landingStats)[0]; isActive: boolean; delay: number }) {
  const resolvedValue =
    "dynamic" in stat && stat.dynamic === "toolCount" ? String(LANDING_TOOL_COUNT) : stat.value;
  const { num, decimals, suffix } = parseStatValue(resolvedValue);
  const count = useCounter(num, isActive);
  const display = decimals > 0 ? count.toFixed(decimals) : Math.round(count).toString();

  return (
    <div
      className="lg:border-r lg:border-black/10 lg:last:border-r-0"
      style={{
        opacity: isActive ? 1 : 0,
        transform: isActive ? "none" : "translateY(20px)",
        transition: `opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
      }}
    >
      <p className="text-[2.75rem] font-semibold leading-none tracking-[-0.08em] text-black sm:text-[5.5rem]">
        {display}{suffix}
      </p>
      <p className="mt-2 text-sm font-semibold uppercase tracking-[0.08em] text-black/45 sm:mt-3 sm:text-base">{stat.label}</p>
    </div>
  );
}

export function LandingStatsStrip() {
  const [sectionRef, isInView] = useInView<HTMLElement>(0.15);

  return (
    <section ref={sectionRef} className="bg-[hsl(var(--lb-bg-soft))] py-4 sm:py-8">
      <div className="lb-container grid gap-8 border-y border-black/10 py-6 sm:grid-cols-2 lg:grid-cols-4">
        {landingStats.map((stat, index) => (
          <AnimatedStat key={stat.label} stat={stat} isActive={isInView} delay={index * 110} />
        ))}
      </div>
    </section>
  );
}
